import React from 'react';
import { Box, Divider, Drawer, IconButton, Paper, Tab, Tabs, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useSelector } from 'react-redux';
import { IPublicAndPrivateSettings } from '@pepperhq/location-sdk';

import { ApplicationState } from 'store/store';
import logger from 'lib/logger';
import { ReviewsGlobalSettings } from './ReviewsGlobalSettings';
import { ReviewsLocationSettings } from './ReviewsLocationSettings';
import { LocationSettingsApi } from 'components/location/LocationSettingsApi';
import { ConfirmDialog } from 'ui/dialogs/ConfirmDialog';
import { ReviewsPreview } from './ReviewsPreview';
import { useSnackbar } from 'notistack';

function updateReviewSettings(
    settings: Partial<IPublicAndPrivateSettings>,
    locationId?: string
): Promise<[void, void, void]> {
    const api = new LocationSettingsApi();
    return Promise.all([
        api.updatePermissionGuardedSetting(
            { reviewsEnabledByMerchant: settings.reviewsEnabledByMerchant },
            'reviewsEnabledByMerchant',
            locationId
        ),
        api.updatePermissionGuardedSetting({ prompts: settings.prompts }, 'prompts', locationId),
        api.updatePermissionGuardedSetting({ reviews: settings.reviews }, 'reviews', locationId)
    ]);
}

export interface ReviewsSettingsFormData {
    globalSettings: {
        cooldownAferDismissalMins: string;
        cooldownAferSubmissionMins: string;
    };
}

interface ReviewsSettingsModalProps {
    open: boolean;
    hasReviewsWritePermission: boolean;
    onClose: () => void;
}

export const ReviewsSettingsPanel = ({
    open,
    onClose,
    hasReviewsWritePermission
}: ReviewsSettingsModalProps) => {
    const { enqueueSnackbar } = useSnackbar();
    const [activeTab, setActiveTab] = React.useState<'global' | 'location' | 'preview'>('global');
    const [globalSettings, setGlobalSettings] = React.useState<IPublicAndPrivateSettings>({});
    const [isGlobalSettingsSaving, setIsGlobalSettingsSaving] = React.useState(false);
    const [globalSettingsLoading, setGlobalSettingsLoading] = React.useState(true);
    const [isLocationSettingsSaving, setIsLocationSettingsSaving] = React.useState(false);
    const [isGlobalDirty, setIsGlobalDirty] = React.useState(false);
    const [isLocationDirty, setIsLocationDirty] = React.useState(false);
    const [isGlobalValid, setIsGlobalValid] = React.useState(true);
    const [isLocationValid, setIsLocationValid] = React.useState(true);
    const [pendingAction, setPendingAction] = React.useState<(() => void) | null>(null);
    const globalSubmitRef = React.useRef<(() => void) | null>(null);
    const locationSubmitRef = React.useRef<(() => void) | null>(null);

    const { locations, isLoading: isLoadingLocations } = useSelector(
        (state: ApplicationState) => state.locations
    );

    React.useEffect(() => {
        async function load() {
            setGlobalSettingsLoading(true);
            const settings = await new LocationSettingsApi().getPublicSettings(null);
            setGlobalSettings(settings);
            setGlobalSettingsLoading(false);
        }
        load();
    }, []);

    const guardUnsavedChanges = React.useCallback(
        (action: () => void) => {
            if (isGlobalDirty || isLocationDirty) {
                setPendingAction(() => action);
            } else {
                action();
            }
        },
        [isGlobalDirty, isLocationDirty]
    );

    const handleConfirm = React.useCallback(() => {
        if (activeTab === 'global') {
            setIsGlobalSettingsSaving(true);
            globalSubmitRef.current?.();
        } else {
            setIsLocationSettingsSaving(true);
            locationSubmitRef.current?.();
        }
    }, [activeTab]);

    const handleCancelDiscard = React.useCallback(() => {
        setPendingAction(null);
        pendingAction?.();
        setIsGlobalDirty(false);
        setIsLocationDirty(false);
    }, [pendingAction, setIsGlobalDirty, setIsLocationDirty]);

    const handleClosePrompt = React.useCallback(() => {
        setPendingAction(null);
    }, []);

    const handleTabChange = React.useCallback(
        (_: React.SyntheticEvent, newValue: string) => {
            guardUnsavedChanges(() => setActiveTab(newValue as 'global' | 'location'));
        },
        [guardUnsavedChanges]
    );

    const handleClose = React.useCallback(() => {
        guardUnsavedChanges(onClose);
    }, [guardUnsavedChanges, onClose]);

    const handleSaveGlobalSettings = React.useCallback(
        async (values: Partial<IPublicAndPrivateSettings>) => {
            setIsGlobalSettingsSaving(true);
            try {
                await updateReviewSettings(values);
                setGlobalSettings(values);
                if (pendingAction) {
                    pendingAction();
                    setPendingAction(null);
                } else {
                    setIsGlobalDirty(false);
                }
                enqueueSnackbar('Default settings saved successfully', { variant: 'success' });
            } catch (error) {
                logger.error(`Error Updating Reviews Global Settings: ${JSON.stringify(error)}`);
                enqueueSnackbar('Failed to save default settings', { variant: 'error' });
                throw error;
            } finally {
                setIsGlobalSettingsSaving(false);
            }
        },
        [enqueueSnackbar, pendingAction]
    );

    const handleSaveLocationSettings = React.useCallback(
        async (values: Partial<IPublicAndPrivateSettings>, locationId: string) => {
            setIsLocationSettingsSaving(true);
            try {
                await updateReviewSettings(values, locationId);
                if (pendingAction) {
                    pendingAction();
                    setPendingAction(null);
                    setIsLocationDirty(false);
                } else {
                    setIsLocationDirty(false);
                }
                enqueueSnackbar('Location settings saved successfully', { variant: 'success' });
            } catch (error) {
                logger.error(`Error Updating Reviews Location Settings: ${JSON.stringify(error)}`);
                enqueueSnackbar('Failed to save location settings', { variant: 'error' });
                throw error;
            } finally {
                setIsLocationSettingsSaving(false);
            }
        },
        [enqueueSnackbar, pendingAction]
    );

    return (
        <Drawer
            variant="temporary"
            open={open}
            onClose={handleClose}
            anchor="right"
            PaperProps={{
                sx: {
                    bgcolor: 'transparent',
                    boxShadow: 'none',
                    width: '600px'
                }
            }}
        >
            <Paper
                sx={{
                    borderRadius: '0px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    height: '100%',
                    overflow: 'auto',
                    position: 'relative'
                }}
            >
                <Box
                    display="flex"
                    flexDirection="column"
                    gap={2}
                    sx={{
                        backdropFilter: 'blur(25px)',
                        position: 'sticky',
                        top: 0,
                        zIndex: 10
                    }}
                >
                    <Box display="flex" justifyContent="space-between" alignItems="center" px={2} pt={2}>
                        <Box>
                            <Typography variant="h6" fontWeight="medium">
                                <b>Review Collection Settings</b>
                            </Typography>
                            <Typography variant="caption">
                                Configure default and location-specific settings
                            </Typography>
                        </Box>
                        <IconButton onClick={handleClose}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                    <Box>
                        <Divider />
                        <Tabs
                            value={activeTab}
                            onChange={handleTabChange}
                            variant="fullWidth"
                            sx={{ borderBottom: 1, borderColor: 'divider' }}
                        >
                            <Tab label="Default Settings" value="global" />
                            <Tab label="Locations" value="location" />
                            <Tab label="Preview" value="preview" />
                        </Tabs>
                    </Box>
                </Box>

                <Box px={2} flex={1}>
                    {activeTab === 'global' && (
                        <ReviewsGlobalSettings
                            globalSettings={globalSettings}
                            isLoading={globalSettingsLoading}
                            isSaving={isGlobalSettingsSaving}
                            onSave={handleSaveGlobalSettings}
                            onDirtyChange={setIsGlobalDirty}
                            onValidityChange={setIsGlobalValid}
                            submitRef={globalSubmitRef}
                            hasReviewsWritePermission={hasReviewsWritePermission}
                        />
                    )}
                    {activeTab === 'location' && (
                        <ReviewsLocationSettings
                            locations={locations}
                            isLoadingLocations={isLoadingLocations}
                            submitRef={locationSubmitRef}
                            onSave={handleSaveLocationSettings}
                            isSaving={isLocationSettingsSaving}
                            onDirtyChange={setIsLocationDirty}
                            onValidityChange={setIsLocationValid}
                            hasReviewsWritePermission={hasReviewsWritePermission}
                        />
                    )}
                    {activeTab === 'preview' && <ReviewsPreview allLocations={locations} />}
                </Box>
            </Paper>
            <ConfirmDialog
                open={pendingAction !== null}
                title="Unsaved Changes"
                content="You have unsaved changes that will be lost if you continue without saving them."
                confirmLabel="Save and Continue"
                cancelLabel="Proceed without saving"
                onConfirm={handleConfirm}
                onCancel={handleCancelDiscard}
                loading={isGlobalSettingsSaving || isLocationSettingsSaving}
                showClose={true}
                onClose={handleClosePrompt}
                confirmDisabled={
                    (activeTab === 'global' && !isGlobalValid) ||
                    (activeTab === 'location' && !isLocationValid)
                }
                confirmDisabledTooltip="Fix the validation errors in the form before saving."
                noBackdropClick
            />
        </Drawer>
    );
};
